import { useEffect, useMemo } from 'react'
import { useDocumentsStore } from '@/stores/documents-store'
import { isDocumentInFlight } from '@/lib/schemas'

/**
 * Keeps the documents store polling while any document on `bidId` is still
 * being processed or indexed. The store owns the 3 s interval; this hook only
 * arms and disarms it.
 *
 * The effect is keyed on a **stable primitive** — the sorted ids of the
 * documents still in flight — not on the `documents` array. The store hands
 * back a brand-new array on every poll response even when nothing changed, so
 * keying on it re-ran the effect's cleanup (`stopPolling`, which clears the
 * pending 3 s timeout) followed by an immediate, undelayed fetch. The loop
 * period became the HTTP round trip rather than the interval (F6: 702 requests
 * in 178 s).
 *
 * Interval ownership ends with the bid or the unmount, never with a state
 * change — hence the separate teardown effect.
 */
export function useDocumentProcessingPoll(bidId: string | undefined): void {
  const documents = useDocumentsStore((state) => state.documents)
  const pollDocuments = useDocumentsStore((state) => state.pollDocuments)
  const stopPolling = useDocumentsStore((state) => state.stopPolling)

  const inFlightKey = useMemo(
    () =>
      documents
        .filter(isDocumentInFlight)
        .map((doc) => doc.id)
        .sort()
        .join('|'),
    [documents]
  )

  useEffect(() => {
    if (!bidId || inFlightKey === '') return
    pollDocuments()
  }, [bidId, inFlightKey, pollDocuments])

  useEffect(() => stopPolling, [bidId, stopPolling])
}
